import { gql } from '../gql';
import React from 'react';
import I18n from 'i18n-js';

import { connect } from '@reverbdotcom/commons/src/connect';

import { redirectToSignin } from '../signup_signin_helper';
import { useUser } from '../user_hooks';
import { isOwner } from '../user_context_helpers';
import CreateOfferModal from './create_offer_modal';
import { MParticleEventName, trackEvent } from '../elog/mparticle_tracker';
import { LISTING_STATES } from '../constants';
import { useURL } from '../url_context';
import { resolveCurrentView } from '../event_tracking/favorites';
import { withCreateUserActionMutation, CoreUserActionsCreateMutationFn } from '../user_actions/create_action_mutation';
import { SellerOpenToOffersPopover } from './seller_open_to_offers_popover';
import {
  ListingCreateOfferButtonDataFragment,
  CreateUserActionMeDataFragment,
  Item2AvailableActionsDataFragment,
  core_apimessages_UserActionName,
  core_apimessages_UserActionStatus,
  BumpKeyFragment,
  arbiter_Interaction_InteractionType,
} from '../gql/graphql';
import { useBumpInteractionTracking } from '../trackBumpInteraction';

/**
 * Bump tracking is conditionally provided by the parent component
 * separately from the required listing fragment.
 *
 */
export interface IExternalProps {
  listing: ListingCreateOfferButtonDataFragment & BumpKeyFragment;
  isSeller?: boolean;
  disabled?: boolean;
  offerCount?: number;
  position?: number;
  parentComponentName?: string;
  className?: string;
  availableActions?: Item2AvailableActionsDataFragment[];
  me?: CreateUserActionMeDataFragment;
}

interface MutationProps {
  createUserAction: CoreUserActionsCreateMutationFn;
}

type IProps = IExternalProps & MutationProps;

export const ListingCreateOfferButtonFragment = gql(`
  fragment ListingCreateOfferButtonData on Listing {
    id
    _id
    state
    offersEnabled
    listingType
    sellerId
    isBuyerOfferEligible
    ...mParticleListingFields
  }
`);

export const Item2LoggedInOffersCountFragment = gql(`
  fragment Item2LoggedInOffersCountData on rql_Me {
    _id
    id
    profile {
      totalBuyerOffersCreated
    }
  }
`);

export function canAcceptOffers(listing: ListingCreateOfferButtonDataFragment) {
  return listing.state === LISTING_STATES.LIVE &&
    listing.offersEnabled &&
    listing.isBuyerOfferEligible;
}

export function availableOfferButtonAction(actions: Item2AvailableActionsDataFragment[]) {
  return actions?.find(action => {
    return action.name === core_apimessages_UserActionName.SELLER_OPEN_TO_OFFERS_CALLOUT
      && action.status === core_apimessages_UserActionStatus.AVAILABLE;
  });
}

const Button = React.forwardRef<HTMLButtonElement, { disabled: boolean, handleClick: (e) => void; }>(
  function Button({ disabled, handleClick }, propRef) {
    return (
      <button
        className="offer-action"
        data-disabled={disabled || null}
        onClick={handleClick}
        type="button"
        ref={propRef}
      >
        {I18n.t('commons.offers.action.make')}
      </button>
    );
  },
);

export function CreateOfferButton(props: IProps) {
  const user = useUser();
  const location = useURL();
  const [openDialog, setOpenDialog] = React.useState(false);
  const bumpInteractionTracking = useBumpInteractionTracking({
    interactionType: arbiter_Interaction_InteractionType.CLICK,
    bumpKey: props?.listing?.bumpKey,
    listingId: props?.listing?.id,
  });

  const disableButton = props.disabled || isOwner(user, { sellerId: props.listing.sellerId });

  React.useEffect(() => {
    if (openDialog) {
      trackEvent({
        eventName: MParticleEventName.ClickedOfferButton,
        listingId: props.listing.id,
        listing: props.listing,
        position: props.position,
        componentName: props.parentComponentName,
        currentView: resolveCurrentView(location),
        role: props.isSeller ? 'seller' : 'buyer',
      });

      bumpInteractionTracking(`${props.parentComponentName}:CreateOfferButton`);
    }
  }, [openDialog]);

  function handleClick(e) {
    // When handle closing the modal, it will blow up because there is no event being passed in
    // so we have to guard
    e.preventDefault();
    e.stopPropagation();

    if (disableButton) {
      return;
    } else if (!user || user.loggedOut) {
      redirectToSignin('Offer');
    } else {
      setOpenDialog(!openDialog);
    }
  }

  if (!canAcceptOffers(props.listing)) {
    return null;
  }

  return (
    <div className={props.className}>
      <SellerOpenToOffersPopover
        availableActions={props.availableActions}
        createUserAction={props.createUserAction}
        me={props.me}
        listing={props.listing}
        currentView={resolveCurrentView(location)}
        anchor={(
          <Button
            disabled={disableButton}
            handleClick={handleClick}
          />
        )}
      />
      {!!props.offerCount &&
        <div
          data-offer-count
          className="size-80 opacity-70 mt-1 align-center"
        >
          {I18n.t('commons.offers.offerCount', { count: props.offerCount })}
        </div>
      }
      <CreateOfferModal
        listingId={props.listing.id}
        isSeller={props.isSeller}
        onCancel={() => { setOpenDialog(false); }}
        isDisplayed={openDialog}
        bumpKey={props.listing.bumpKey} // this bumpKey is passed so we can track the MAKE_AN_OFFER bump interaction on offer create success
      />
    </div>
  );
}

export default connect<IExternalProps>([
  withCreateUserActionMutation,
])(CreateOfferButton);
